import React, {useRef, useState} from 'react'

import ReactCrop, {centerCrop, makeAspectCrop, PixelCrop} from 'react-image-crop'
import {useUploadFile} from 'src/feature/bite/context/useMutation'
import {canvasPreview} from '../helpers/canvasPreview'
import {useDebounceEffect} from './useDebounceEffect'

import 'react-image-crop/dist/ReactCrop.css'
import {toast} from 'react-toastify'

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  )
}

export default function CropAspect({
  values,
  crop,
  setCrop,
  imgSrc,
  setImgSrc,
  setResizeImageModal,
  setFieldValue,
  selectedImageIndex,
}: any) {
  const previewCanvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const scale = 1
  const {mutateAsync: uploadFile} = useUploadFile()

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const {width, height} = e.currentTarget
    const originalWidth = imgRef.current?.naturalWidth || 0
    const originalHeight = imgRef.current?.naturalHeight || 0

    if (originalWidth < 98 || originalHeight < 98) {
      toast.error('Image is too small. Height and width must be at least 98px.')
      setResizeImageModal(false)
      setImgSrc('')
      return
    }

    setCrop(centerAspectCrop(width, height, 1 / 1))
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale)
      }
    },
    100,
    [completedCrop, scale, imgRef.current, previewCanvasRef.current]
  )

  const handleCanvasToFile = (): Promise<File | null> => {
    return new Promise((resolve) => {
      const canvas = previewCanvasRef.current
      if (!canvas) {
        resolve(null)
        return
      }

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'image.webp', {type: blob.type})
          resolve(file)
        } else {
          resolve(null)
        }
      }, 'image/webp')
    })
  }

  return (
    <div className='App'>
      {!!imgSrc && (
        <div>
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={(c) => setCompletedCrop(c)}
            minWidth={40}
            minHeight={40}
          >
            <img
              ref={imgRef}
              alt='Crop'
              src={imgSrc}
              onLoad={onImageLoad}
              onError={() => {
                toast.error('Image is too small. Height and width must be at least 98px.')
                setResizeImageModal(false)
                setImgSrc('')
              }}
              style={{
                maxHeight: '300px',
                maxWidth: '100%',
                transform: `scale(${scale})`,
                objectFit: 'contain',
              }}
            />
          </ReactCrop>

          {!!completedCrop && (
            <div className='d-flex flex-column justify-content-center align-items-center gap-10 w-100 mt-5'>
              <div className='d-flex flex-column justify-content-center align-items-center w-100'>
                <h5 className='text-center'>Preview:</h5>

                <canvas
                  ref={previewCanvasRef}
                  style={{
                    maxHeight: '300px',
                    maxWidth: '100%',
                  }}
                />
              </div>
            </div>
          )}

          <div className='d-flex justify-content-center align-items-center gap-5 w-100 mt-10'>
            <button
              className='btn btn-primary'
              onClick={async () => {
                const file = (await handleCanvasToFile()) as any
                if (!file) return

                const upload = await uploadFile(file)

                if (upload && upload.data) {
                  const newImage = {
                    imageUrl: upload.data.name,
                    imageType: 2,
                    imageFullUrl: process.env.REACT_APP_IMG_DOMAIN + upload.data.name,
                  }

                  const updatedImages = [...(values.imagesData || [])]
                  if (
                    values.imagesData &&
                    values.imagesData.length > 0 &&
                    values.imagesData?.[selectedImageIndex]
                  ) {
                    updatedImages[selectedImageIndex] = newImage
                  } else {
                    updatedImages.push(newImage)
                  }

                  setFieldValue('imagesData', updatedImages)
                  setImgSrc('')
                  setResizeImageModal(false)
                }
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
